import { useState } from "react";

import { useHistory } from "react-router-dom";
import { ClusterType, EarlyAccessFeature } from "@doitintl/cmp-models";
import { Skeleton, Stack, Typography } from "@mui/material";

import { FilterTable } from "../../../../Components/FilterTable/FilterTable";
import {
  type FilterTableItemDescriptor,
  generateHeadersAndFilters,
} from "../../../../Components/FilterTable/filterTableUtils";
import { type FilterTableToolbarProps } from "../../../../Components/FilterTable/Toolbar/FilterTableToolbar";
import { useDarkThemeCheck } from "../../../../Components/hooks/useDarkThemeCheck";
import useRouteMatchURL from "../../../../Components/hooks/useRouteMatchURL";
import { useAuthContext } from "../../../../Context/AuthContext";
import { useCustomerContext } from "../../../../Context/CustomerContext";
import { ClusterDialog } from "../ClusterDialog";
import ClusterPageHeader from "../Header";
import ClusterTableRow from "./ClusterRow";
import { type ClusterTableItem } from "./types";
import { sortedValueToOption } from "./utils";

interface ClusterTableProps {
  clusters: ClusterTableItem[];
  loading: boolean;
}

type ExtendedFilterTableItemDescriptor<T> = FilterTableItemDescriptor<T> & {
  featureFlag?: EarlyAccessFeature;
};

const ClusterTable = ({ clusters, loading }: ClusterTableProps) => {
  const history = useHistory();
  const routeMatchURL = useRouteMatchURL();
  const { customer } = useCustomerContext();
  const { isDoitEmployee } = useAuthContext();
  const isDarkTheme = useDarkThemeCheck();

  const [clusterDialogIsOpen, setClusterDialogIsOpen] = useState(false);

  const canAccessK8SFeatures =
    isDoitEmployee || customer.earlyAccessFeatures?.includes(EarlyAccessFeature.K8S_FEATURES);

  const openClusterDialog = () => {
    setClusterDialogIsOpen(true);
  };

  const getProjectIdHeaderText = () => {
    const hasGkeClusters = clusters.some((cluster) => cluster.type === ClusterType.GKE);
    const hasEksClusters = clusters.some((cluster) => cluster.type === ClusterType.EKS);

    if (hasGkeClusters && !hasEksClusters) return "Project";
    if (hasEksClusters && !hasGkeClusters) return "Account";
    return "Account/Project";
  };

  const itemDescriptions: ExtendedFilterTableItemDescriptor<ClusterTableItem>[] = [
    {
      headerText: "Name",
      value: "name",
      filterType: "string",
    },
    {
      headerText: getProjectIdHeaderText(),
      value: "projectId",
      filterType: "string",
    },
    {
      headerText: "Status",
      value: "computedStatus",
      filterType: "string",
      filterOptions: {
        toOption: sortedValueToOption,
      },
      featureFlag: EarlyAccessFeature.K8S_FEATURES,
    },
    {
      headerText: "Type",
      value: "type",
      filterType: "string",
    },
    {
      headerText: "Region",
      value: "region",
      filterType: "string",
    },
    {
      headerText: "Version",
      value: "kubernetesControlPlaneVersion",
      filterType: "string",
    },
    {
      headerText: "Agent status",
      value: "agentStatus",
      filterType: "string",
      filterOptions: {
        toOption: sortedValueToOption,
      },
      featureFlag: EarlyAccessFeature.FSK8S,
    },
    {
      headerText: "Enabled features",
      value: "enabledFeatures",
      filterType: "string",
      featureFlag: EarlyAccessFeature.K8S_FEATURES,
    },
    {
      value: "@",
      headerText: "Action",
      disableSorting: true,
      headerStyle: canAccessK8SFeatures
        ? {
            width: "16px",
            align: "right",
          }
        : undefined,
    },
  ];

  const availableItemDescriptions: FilterTableItemDescriptor<ClusterTableItem>[] = itemDescriptions.filter(
    (item) => !item.featureFlag || isDoitEmployee || customer.earlyAccessFeatures?.includes(item.featureFlag)
  );

  const { headerColumns, filters } = generateHeadersAndFilters(availableItemDescriptions);

  const toolbarProps: FilterTableToolbarProps = {
    title: <ClusterPageHeader />,
    allowToEditColumns: true,
  };

  if (canAccessK8SFeatures) {
    toolbarProps.primaryButton = {
      onClick: openClusterDialog,
      text: "Connect self-managed cluster",
      "data-cy": "connectSelfManagedClusterButton",
    };

    toolbarProps.secondaryButtons = [
      {
        onClick: () => {
          history.push(`${routeMatchURL}/activate/bulk`);
        },
        text: "Enable features",
        "data-cy": "enableFeaturesButton",
      },
    ];
  }

  if (loading) {
    return <Skeleton variant="rectangular" height={400} />;
  }

  return (
    <Stack>
      <ClusterDialog open={clusterDialogIsOpen} setOpen={setClusterDialogIsOpen} />
      <FilterTable<ClusterTableItem>
        rowComponent={ClusterTableRow}
        persistenceKey="k8s-clusters-table"
        toolbarProps={toolbarProps}
        tableItems={clusters}
        headerColumns={headerColumns}
        filterColumns={filters}
        defaultSortingColumnValue="computedStatus"
        defaultSortDirection="asc"
        emptyTableComponent={
          <Typography variant="body2" textAlign="center">
            No clusters found
          </Typography>
        }
        tableProps={{
          sx: {
            "& thead th:last-child": {
              position: "sticky",
              right: 0,
              zIndex: 2,
            },
            "& tbody td:last-child": {
              position: "sticky",
              right: 0,
              backgroundColor: "background.default",
              zIndex: 1,
            },
            "& tbody tr:hover td:last-child": {
              backgroundColor: isDarkTheme ? "#45454f" : "#f5f5f5",
            },
          },
        }}
      />
    </Stack>
  );
};

export default ClusterTable;
